import {Sprite} from 'pixi.js';
import {CoreDI, FullScreenFader, inject, ObjectBuilder, ScreenParameters, Tween, UpdateManager} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {View} from '@gongxigames/framework/slot';

export class WaitingView extends View<GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('WaitingView');
    public static layer = LAYERS.SYSTEM_POPUPS;

    public static readonly FADER: any = {
        alpha: 0.75,
        duration: 250
    };

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _updater: UpdateManager = new UpdateManager(true);
    protected _fader!: FullScreenFader;
    protected _loaderContainer!: Sprite;
    protected _loadingIcon!: Sprite;
    protected _tween?: Tween;

    public init(): void {
        super.init();

        this.updateManager.add(this._updater);
        this.initGraphic();
    }

    protected initGraphic(): void {
        this.initFader();
        this.initLoader();
        this.hide();
    }

    public showPopup(): void {
        this.forceUpdateOrientation();
        this.show();
        this.fade(WaitingView.FADER.alpha);
    }

    public hidePopup(): void {
        this.hide();
        this._updater.removeAll();
        this._tween = undefined;
    }

    protected initLoader(): void {
        this._loaderContainer = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.waitingPopup);
        this._loadingIcon = this._loaderContainer.children[0] as Sprite;
        this._loadingIcon.anchor.set(0.5);
        this._loadingIcon.x = this._gameConfig.mainFrame.width / 2;
        this._loadingIcon.y = this._gameConfig.mainFrame.height / 2;

        this.container.addChild(this._loadingIcon);
    }

    protected initFader(): void {
        this._fader = this._displayObjectUtils.createFullScreenFader({alpha: WaitingView.FADER.finish});

        this.container.addChild(this._fader);
    }

    protected fade(alpha: number): void {
        this._tween = new Tween(this._fader)
            .to({alpha}, WaitingView.FADER.duration)
            .onComplete(() => {
                if (alpha > 0) {
                    this.playLoading();
                } else {
                    this.hidePopup();
                }
            })
            .start();

        this._updater.add(this._tween);
    }

    protected playLoading(): void {
        let rotateAngle = 0;

        this._tween = new Tween()
            .to({}, 9999999)
            .onUpdate(() => {
                const PI = Math.PI;

                rotateAngle += PI / 25;

                if (rotateAngle > 2 * PI) {
                    rotateAngle -= 2 * PI;
                }

                this._loadingIcon.rotation = rotateAngle;
            })
            .start();

        this._updater.add(this._tween);
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this._loaderContainer);
    }

    public onPortrait(): void {
        super.onPortrait();

        this._loadingIcon.x = this._gameConfig.viewport.mobile.height / 2;
        this._loadingIcon.y = this._gameConfig.viewport.mobile.width / 2;
    }

    public onLandscape(): void {
        super.onLandscape();

        this._loadingIcon.x = this._gameConfig.viewport.mobile.width / 2;
        this._loadingIcon.y = this._gameConfig.viewport.mobile.height / 2;
    }

    public onResized(screenParams: ScreenParameters): void {
        this._fader?.resize(screenParams);
    }
}
